import * as React from "react";
import {useEffect, useState} from "react";
import Plan from "../../../model/Plan";
import Model from "../../../model/Model";
import PlanChange, {PlanChangeType} from "../../../model/PlanChange";
import Person from "../../../model/Person";
import LifeInsuranceStrategyChange, {
  ILifeInsurancePlan,
  ILifeInsuranceStrategyChange,
  OutsideEstateId,
  StandardTerms,
  computeEndDate,
} from "../../../model/changes/LifeInsuranceStrategyChange";
import {
  formatMonthAge,
  getISODateFromDate,
  getISODateToday,
  isoToLocalDate, isoToLocalDateString,
  moneyToNumberFormat,
  numberToMoneyFormat,
} from "../../../stores/StoreUtilities";
import {differenceInMonths} from "date-fns";
import {useResources} from "../../../stores/ResourceProvider";
import {
  Box,
  DialogContentText,
  Divider,
  Grid,
  IconButton,
  Typography,
  useTheme
} from "@mui/material";
import {AddCircleRounded} from "@mui/icons-material";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import AccordionSection, {AccordionSectionSummary, AccordionSectionDetails} from "../../../components/mui/AccordionSection";
import MilestoneDateFieldValidator from "../../../components/form/MilestoneDateFieldValidator";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {CreatePlanChangeInput, UpdatePlanChangeInput} from "../../../API";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import IconicButton from "../../../components/controls/IconicButton";
import Tracking from "../../../components/Tracking";
import {Frequency} from "../../../model/Schedule";
import Milestone from "../../../model/Milestone";
import Asset, {AssetCategory} from "../../../model/Asset";
import {globalColors} from "../../../styles/globalStyles";

const CUSTOM_TERM = "Custom"
const termOptions = [...StandardTerms.map(t => `${t}`), CUSTOM_TERM]
const policyTypeOptions = ["Term"]

class LifeInsurancePolicyEditDetail {
  ownerId: string
  owner?: Person
  coverageAmount: string
  startDate: string
  termSelection: string
  customTermYears: string
  premiumAmount: string
  premiumFrequency: Frequency
  depositAssetId: string
  notes: string
  ageMilestones: Milestone[]

  constructor(data: any) {
    this.ownerId = data.ownerId ?? ""
    this.owner = data.owner
    this.coverageAmount = data.coverageAmount ?? ""
    this.startDate = data.startDate ?? getISODateToday()
    this.termSelection = data.termSelection ?? "20"
    this.customTermYears = data.customTermYears ?? ""
    this.premiumAmount = data.premiumAmount ?? ""
    this.premiumFrequency = data.premiumFrequency ?? Frequency.Monthly
    this.depositAssetId = data.depositAssetId ?? ""
    this.notes = data.notes ?? ""
    this.ageMilestones = data.ageMilestones ?? []
  }
}

const LifeInsuranceStrategyDialog = ({
  open,
  plan,
  model,
  onClose,
  onSave
}: {
  open?: boolean
  plan: Plan
  model: Model
  onClose?(): void
  onSave?(plan: Plan, update: PlanChange): void
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(open === true)
  const [planChange, setPlanChange] = useState<LifeInsuranceStrategyChange | undefined>()
  const [editPolicies, setEditPolicies] = useState<LifeInsurancePolicyEditDetail[]>([])
  const [expandedPolicies, setExpandedPolicies] = useState<Set<number>>(new Set())
  const [formKey, setFormKey] = useState(0)

  const { modelStore, notify, confirm } = useResources()

  const theme = useTheme()
  const styles = {
    section: {
      backgroundColor: globalColors.details,
      marginBottom: "4px",
      paddingLeft: "4px",
      paddingRight: "4px"
    },
    autoField: {
      marginTop: "8px"
    }
  }

  const ownerOptions = model.persons.filter((p: Person) => !p.isJoint)
  const isSinglePerson = ownerOptions.length === 1

  const initTermSelection = (termYears: number | undefined): { selection: string, custom: string } => {
    if (termYears === undefined) {
      return { selection: "20", custom: "" }
    }
    if (StandardTerms.includes(termYears)) {
      return { selection: String(termYears), custom: "" }
    }
    return { selection: CUSTOM_TERM, custom: String(termYears) }
  }

  useEffect(() => {
    setIsOpen(open === true)
    let change: LifeInsuranceStrategyChange = plan.getChange(PlanChangeType.LifeInsuranceStrategy) as LifeInsuranceStrategyChange
    if (!change) {
      change = new LifeInsuranceStrategyChange({
        accountId: plan.accountId,
        userId: model.userId,
        modelId: model.id,
        planId: plan.id,
        changeType: PlanChangeType.LifeInsuranceStrategy,
        name: "Life Insurance Strategy",
        description: "",
        enabled: true,
        details: JSON.stringify({ plans: [] })
      })
    }
    if (!change.plans) {
      change.plans = []
    }

    const editDetails: LifeInsurancePolicyEditDetail[] = []
    change.plans.forEach((p: ILifeInsurancePlan) => {
      const owner = model.persons.find((person: Person) => person.id === p.ownerId)
      if (!owner) return

      const term = initTermSelection(p.termYears)
      editDetails.push(new LifeInsurancePolicyEditDetail({
        ownerId: p.ownerId,
        owner: owner,
        coverageAmount: numberToMoneyFormat(p.coverageAmount),
        startDate: p.startDate ?? getISODateToday(),
        termSelection: term.selection,
        customTermYears: term.custom,
        premiumAmount: numberToMoneyFormat(p.premiumAmount),
        premiumFrequency: p.premiumFrequency || Frequency.Monthly,
        depositAssetId: p.depositAssetId ?? "",
        notes: p.notes ?? "",
        ageMilestones: owner.getAgeMilestones(20),
      }))
    })

    setEditPolicies(editDetails)
    setExpandedPolicies(new Set())
    setPlanChange(change)
  }, [plan, open])

  const getMilestoneDate = (value: string | undefined | null, milestones: Milestone[]) => {
    if (value == null || value === "") {
      return ""
    }
    let isoDate = ""
    if (value.startsWith("age")) {
      const milestone = milestones.find((m: Milestone) => m.id === value)
      if (milestone) {
        isoDate = milestone.date
      }
    } else {
      isoDate = value
    }
    return isoDate
  }

  const getTermYears = (termSelection: string, customTermYears: string): number | undefined => {
    if (termSelection === CUSTOM_TERM) {
      const n = parseInt(customTermYears)
      return isNaN(n) ? undefined : n
    }
    const n = parseInt(termSelection)
    return isNaN(n) ? undefined : n
  }

  const getCoverageEndHelperText = (startDate?: string, termSelection?: string, customTermYears?: string) => {
    const years = getTermYears(termSelection ?? "20", customTermYears ?? "")
    const endDate = computeEndDate(startDate, years)
    if (endDate) {
      return `Coverage ends: ${isoToLocalDateString(endDate)}`
    }
    return ""
  }

  const getDepositOptions = (policyOwnerId: string): (Asset | string)[] => {
    const options: (Asset | string)[] = []

    let eligibleAssets: Asset[]
    if (isSinglePerson) {
      // Single-person models: show all liquid investable assets
      eligibleAssets = model.assets.filter((a: Asset) =>
        a.assetCategory === AssetCategory.LiquidInvestableAssets && !a.archived &&
        !a.assetTypeDef.hasIncomeRider
      )
    } else {
      // Coupled models: exclude assets exclusively owned by the insured person
      eligibleAssets = model.assets.filter((a: Asset) =>
        a.assetCategory === AssetCategory.LiquidInvestableAssets &&
        !a.archived &&
        !a.assetTypeDef.hasIncomeRider &&
        a.ownerId !== policyOwnerId
      )
    }
    Model.sortAssets(eligibleAssets)
    options.push(...eligibleAssets)

    options.push(OutsideEstateId)

    return options
  }

  const getDepositValue = (depositAssetId: string): Asset | string | null => {
    if (depositAssetId === OutsideEstateId) {
      return OutsideEstateId
    }
    if (depositAssetId) {
      return model.assets.find((a: Asset) => a.id === depositAssetId) || null
    }
    return null
  }

  const handleAdd = () => {
    const defaultOwner = ownerOptions[0]
    const newPolicy = new LifeInsurancePolicyEditDetail({
      ownerId: defaultOwner?.id ?? "",
      owner: defaultOwner,
      ageMilestones: defaultOwner?.getAgeMilestones() ?? [],
    })
    const newIndex = editPolicies.length
    setEditPolicies([...editPolicies, newPolicy])
    setExpandedPolicies(new Set([...Array.from(expandedPolicies), newIndex]))
  }

  const toggleExpanded = (index: number) => {
    const next = new Set(expandedPolicies)
    if (next.has(index)) {
      next.delete(index)
    } else {
      next.add(index)
    }
    setExpandedPolicies(next)
  }

  const handleDelete = (index: number) => {
    confirm.show(
      'Remove Policy',
      'Are you sure you want to remove this insurance policy?',
      ['Remove', 'Cancel'],
      () => {
        editPolicies.splice(index, 1)
        setEditPolicies([...editPolicies])
        const next = new Set<number>()
        Array.from(expandedPolicies).forEach((i) => {
          if (i < index) next.add(i)
          else if (i > index) next.add(i - 1)
        })
        setExpandedPolicies(next)
        setFormKey(prev => prev + 1)
        return true
      }
    )
  }

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    try {
      let change: PlanChange | undefined

      const details: ILifeInsuranceStrategyChange = {
        plans: editPolicies
          .filter((p: LifeInsurancePolicyEditDetail) => p.ownerId)
          .map((p: LifeInsurancePolicyEditDetail) => {
            const termYears = getTermYears(p.termSelection, p.customTermYears)
            return {
              ownerId: p.ownerId,
              coverageAmount: moneyToNumberFormat(p.coverageAmount, 0) ?? 0,
              startDate: p.startDate,
              termYears: termYears,
              coverageEndDate: computeEndDate(p.startDate, termYears),
              premiumAmount: moneyToNumberFormat(p.premiumAmount, 0) ?? 0,
              premiumFrequency: p.premiumFrequency,
              depositAssetId: p.depositAssetId || undefined,
              notes: p.notes || undefined,
            } as ILifeInsurancePlan
          })
      }

      if (planChange && !planChange.id) {
        const input: CreatePlanChangeInput = {
          accountId: planChange.accountId,
          userId: planChange.userId,
          modelId: planChange.modelId,
          planId: planChange.planId,
          changeType: planChange.changeType,
          name: planChange.name,
          description: generateDescription(),
          enabled: true,
          details: JSON.stringify(details)
        }
        change = await modelStore.createPlanChange(input, true)
        Tracking.event({action: "Life Insurance Strategy Created"})
      } else if (planChange) {
        const input: UpdatePlanChangeInput = {
          id: planChange.id,
          description: generateDescription(),
          enabled: true,
          details: JSON.stringify(details)
        }
        change = await modelStore.updatePlanChange(input)
        Tracking.event({action: "Life Insurance Strategy Updated"})
      }

      if (onSave && change) {
        onSave(plan, change)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const generateDescription = () => {
    const validPolicies = editPolicies.filter((p: LifeInsurancePolicyEditDetail) => p.ownerId && p.coverageAmount)
    if (validPolicies.length === 0) return "No policies"

    const parts = validPolicies.map((p: LifeInsurancePolicyEditDetail) => {
      const years = getTermYears(p.termSelection, p.customTermYears)
      return `${p.owner?.nickname ?? "?"}: ${years ?? "?"}yr Term ${p.coverageAmount}`
    })

    return parts.join(", ")
  }

  const getPolicySummary = (policy: LifeInsurancePolicyEditDetail): string => {
    const ownerName = policy.owner?.nickname ?? "?"
    const years = getTermYears(policy.termSelection, policy.customTermYears)
    const raw = policy.coverageAmount || ""
    const amount = raw ? (numberToMoneyFormat(moneyToNumberFormat(raw, 0), 0) ?? raw) : ""
    if (years && amount) {
      return `${ownerName}: ${years}yr Term ${amount}`
    }
    return `${ownerName}: Term`
  }

  const renderPolicyCard = (policy: LifeInsurancePolicyEditDetail, index: number) => {
    const isExpanded = expandedPolicies.has(index)
    const policyKey = `lifeinspolicy-${index}`

    return (
      <AccordionSection key={policyKey} expanded={isExpanded} onChange={() => toggleExpanded(index)}>
        <AccordionSectionSummary
          expandIcon={<ArrowForwardIosSharpIcon sx={{fontSize: '0.9rem'}}/>}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Typography variant="body2">{getPolicySummary(policy)}</Typography>
            <IconicButton onClick={(e: any) => { e.stopPropagation(); handleDelete(index) }}>
              <DeleteIcon fontSize="small"/>
            </IconicButton>
          </Box>
        </AccordionSectionSummary>
        <AccordionSectionDetails>
          <Grid container spacing={1}>
            {/* Header: Owner, Type */}
            <Grid item xs={6} sx={styles.autoField}>
              <TextFieldValidator
                type="text"
                validators={{required: true}}
                name={`owner_${index}`}
                label="Policy Owner"
                variant="standard"
                autocompleteOptions={{
                  freeSolo: false,
                  disableClearable: true,
                  options: ownerOptions,
                  getOptionLabel: (option: Person) => option ? option.nickname : "",
                  isOptionEqualToValue: (option: Person, value: Person) => option.id === value.id,
                  value: policy.owner || null,
                  onChange: (event: any, value: Person) => {
                    editPolicies[index].ownerId = value.id
                    editPolicies[index].owner = value
                    editPolicies[index].ageMilestones = value.getAgeMilestones()
                    editPolicies[index].depositAssetId = ""
                    setEditPolicies([...editPolicies])
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sx={styles.autoField}>
              <TextFieldValidator
                type="text"
                validators={{required: true}}
                name={`policyType_${index}`}
                label="Type"
                variant="standard"
                autocompleteOptions={{
                  freeSolo: false,
                  disableClearable: true,
                  options: policyTypeOptions,
                  value: "Term",
                  onChange: (event: any, value: string) => {
                    // Future: handle different policy types
                  }
                }}
              />
            </Grid>

            {/* Policy Section */}
            <Grid item xs={12}>
              <Box sx={styles.section}>
                <Typography variant="h6" sx={{pb: 0}}>Policy</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextFieldValidator
                      type="string"
                      validators={{required: true, isMoney: true}}
                      name={`coverageAmount_${index}`}
                      variant="standard"
                      margin="dense"
                      label="Death Benefit Amount"
                      value={policy.coverageAmount}
                      fullWidth
                      onChange={(event: any) => {
                        editPolicies[index].coverageAmount = event.target.value
                        setEditPolicies([...editPolicies])
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sx={styles.autoField}>
                    <TextFieldValidator
                      type="text"
                      validators={{required: false}}
                      name={`depositAsset_${index}`}
                      label="Deposit Destination"
                      variant="standard"
                      autocompleteOptions={{
                        freeSolo: false,
                        options: getDepositOptions(policy.ownerId),
                        getOptionLabel: (option: Asset | string) => {
                          if (typeof option === 'string' && option === OutsideEstateId) {
                            return "Outside of Estate (i.e., charity)"
                          }
                          if (typeof option === 'object' && option !== null) {
                            return (option as Asset).description
                          }
                          return ""
                        },
                        isOptionEqualToValue: (option: Asset | string, value: Asset | string) => {
                          if (typeof option === 'string' && typeof value === 'string') return option === value
                          if (typeof option === 'string' || typeof value === 'string') return false
                          return (option as Asset).id === (value as Asset).id
                        },
                        value: getDepositValue(policy.depositAssetId),
                        onChange: (event: any, value: Asset | string | null) => {
                          if (typeof value === 'string') {
                            editPolicies[index].depositAssetId = OutsideEstateId
                          } else if (value) {
                            editPolicies[index].depositAssetId = (value as Asset).id
                          } else {
                            editPolicies[index].depositAssetId = ""
                          }
                          setEditPolicies([...editPolicies])
                        }
                      }}
                    />
                  </Grid>
                </Grid>
                {policy.depositAssetId === OutsideEstateId &&
                  <Typography variant="caption" sx={{display: "block", color: theme.palette.grey["600"], pb: 0.5}}>
                    Benefit will not be added to the plan
                  </Typography>
                }
              </Box>
            </Grid>

            {/* Term Section */}
            <Grid item xs={12}>
              <Box sx={styles.section}>
                <Typography variant="h6" sx={{pb: 0}}>Term</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <MilestoneDateFieldValidator
                      name={`startDate_${index}`}
                      label="Start Date"
                      milestones={policy.ageMilestones}
                      value={policy.startDate}
                      required={true}
                      width="100%"
                      helperText={policy.owner?.birthDate && policy.startDate
                        ? `Age: ${formatMonthAge(differenceInMonths(isoToLocalDate(policy.startDate), isoToLocalDate(policy.owner.birthDate)))}`
                        : undefined}
                      onChange={(value: string) => {
                        editPolicies[index].startDate = getMilestoneDate(value, policy.ageMilestones)
                        setEditPolicies([...editPolicies])
                      }}
                    />
                  </Grid>
                  <Grid item xs={policy.termSelection === CUSTOM_TERM ? 3 : 6} mt={1}>
                    <TextFieldValidator
                      type="text"
                      validators={{required: true}}
                      name={`termYears_${index}`}
                      label="Term (years)"
                      variant="standard"
                      helperText={getCoverageEndHelperText(policy.startDate, policy.termSelection, policy.customTermYears)}
                      autocompleteOptions={{
                        freeSolo: false,
                        disableClearable: true,
                        options: termOptions,
                        value: policy.termSelection || null,
                        onChange: (event: any, value: string, reason: any) => {
                          editPolicies[index].termSelection = value
                          if (value !== CUSTOM_TERM) {
                            editPolicies[index].customTermYears = ""
                          }
                          setEditPolicies([...editPolicies])
                        },
                      }}
                    />
                  </Grid>
                  {policy.termSelection === CUSTOM_TERM &&
                    <Grid item xs={3}>
                      <TextFieldValidator
                        type="string"
                        validators={{required: true, minValue: 1, maxValue: 50}}
                        name={`customTermYears_${index}`}
                        variant="standard"
                        margin="dense"
                        label="Years"
                        value={policy.customTermYears}
                        fullWidth
                        onChange={(event: any) => {
                          editPolicies[index].customTermYears = event.target.value
                          setEditPolicies([...editPolicies])
                        }}
                      />
                    </Grid>
                  }
                </Grid>
              </Box>
            </Grid>

            {/* Premium Section */}
            <Grid item xs={12}>
              <Box sx={styles.section}>
                <Typography variant="h6" sx={{pb: 0}}>Premium</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextFieldValidator
                      type="string"
                      validators={{required: true, isMoney: true}}
                      name={`premiumAmount_${index}`}
                      variant="standard"
                      margin="dense"
                      label="Premium Amount"
                      value={policy.premiumAmount}
                      fullWidth
                      onChange={(event: any) => {
                        editPolicies[index].premiumAmount = event.target.value
                        setEditPolicies([...editPolicies])
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} mt={1}>
                    <TextFieldValidator
                      type="text"
                      validators={{required: true}}
                      name={`premiumFrequency_${index}`}
                      label="Frequency"
                      variant="standard"
                      autocompleteOptions={{
                        freeSolo: false,
                        disableClearable: true,
                        options: [Frequency.Monthly, Frequency.Yearly],
                        value: policy.premiumFrequency || null,
                        onChange: (event: any, value: Frequency, reason: any) => {
                          editPolicies[index].premiumFrequency = value
                          setEditPolicies([...editPolicies])
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Notes Section */}
            <Grid item xs={12}>
              <TextFieldValidator
                margin="normal"
                name={`notes_${index}`}
                label="Notes"
                type="text"
                fullWidth
                variant="outlined"
                multiline={true}
                minRows={6}
                maxRows={6}
                InputLabelProps={{shrink: true, sx:{mt:-1}}}
                InputProps={{
                  sx: {
                    fontSize: 14,
                    lineHeight: 1.3,
                    py: 1,
                    mt: -1
                  }
                }}
                validators={{required: false}}
                value={policy.notes}
                onChange={(event: any) => {
                  editPolicies[index].notes = event.target.value
                  setEditPolicies([...editPolicies])
                }}
              />
            </Grid>
          </Grid>
        </AccordionSectionDetails>
      </AccordionSection>
    )
  }

  if (!planChange) {
    return null
  }

  return (
    <ModelEditDialog key={formKey} title="Life Insurance Strategy" open={isOpen} size="sm"
                     onCancel={handleClose}
                     onSave={handleSave}
    >
      <DialogContentText sx={{mt: 1, fontSize: 14}}>
        Model life insurance policies to evaluate if coverage is needed and compare the plan with and without it.
        You may test other life expectancies by overriding Life Expectancy in the Timeline Strategy.
      </DialogContentText>
      {editPolicies.map((policy: LifeInsurancePolicyEditDetail, index: number) =>
        renderPolicyCard(policy, index)
      )}
      <Box display="flex" justifyContent="center" mt={1} mb={1}>
        <IconButton sx={{borderRadius: 1}} onClick={handleAdd}>
          <AddCircleRounded sx={{color: theme.palette.secondary.light}}/>
          <Typography variant="body2" sx={{color: theme.palette.grey["600"], ml: 1}}>
            {editPolicies.length > 0 ? "Add Another Policy" : "Add Insurance Policy"}
          </Typography>
        </IconButton>
      </Box>
    </ModelEditDialog>
  )
}

export default LifeInsuranceStrategyDialog
